import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { useMigrationStore, CloseGateError } from '@/stores/migrationStore'
import { assetIdsOf, canClose, isBlocked, fmtDay } from '@/lib/migration/engine'
import { STATUS_TOKEN } from '@/lib/migration/summary'
import { ITEM_STATUSES, ITEM_STATUS_LABEL, type MigrationItem, type MigrationItemStatus } from '@/api/types/migration'
import { Chip, Icon } from '@/components/readiness/primitives'
import { StatusChip } from '@/components/migration/primitives'
import { formatDate } from '@/components/common/format'

// Migration item drawer (I3 §8.1): 480px, right-hand, the same frame as the
// I2 §5 quick view. Status stepper, the close-out gate panel, details, scope,
// agility flags for the item's system, and what the item feeds in the plan.
// Esc closes; ←/→ move through the current list order.

export function MigrationItemDrawer({ ctx, item, prev, next, onNavigate, onClose }: { ctx: MigrationCtx; item: MigrationItem; prev: string | null; next: string | null; onNavigate: (id: string) => void; onClose: () => void }) {
  const { orgId, indexId, actor, mscope } = ctx
  const ref = useRef<HTMLDivElement>(null)
  const setStatus = useMigrationStore((s) => s.setItemStatus)
  const updateItem = useMigrationStore((s) => s.updateItem)
  const setScope = useMigrationStore((s) => s.setItemScope)
  const setDependency = useMigrationStore((s) => s.setDependency)
  const requestRescan = useMigrationStore((s) => s.requestRescan)
  const closeItem = useMigrationStore((s) => s.closeItem)
  const setAgility = useMigrationStore((s) => s.setAgility)
  const [editScope, setEditScope] = useState(false)
  const [gateMsg, setGateMsg] = useState<string | null>(null)

  useEffect(() => {
    ref.current?.focus()
    setEditScope(false)
    setGateMsg(null)
  }, [item.id])
  useEffect(() => {
    const onDoc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onDoc)
    return () => document.removeEventListener('keydown', onDoc)
  }, [onClose])
  const onKey = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    if (e.key === 'ArrowLeft' && prev) onNavigate(prev)
    else if (e.key === 'ArrowRight' && next) onNavigate(next)
  }
  if (!mscope) return null
  const facts = mscope.facts
  const system = facts?.systems.find((s) => s.id === item.system_id) ?? null
  const wave = facts?.waves.find((w) => w.id === item.wave_id) ?? null
  const scoped = assetIdsOf(item.id, mscope.item_assets)
  const gate = canClose(item, scoped, mscope.item_events, mscope.rescans)
  const closed = item.status === 'closed'
  const stepIndex = ITEM_STATUSES.indexOf(item.status)
  const agility = mscope.agility.find((a) => a.system_id === item.system_id)
  const systemAssets = (facts?.assets ?? []).filter((a) => a.system_id === item.system_id && a.quantum_vulnerable)
  const dep = mscope.item_deps.find((d) => d.item_id === item.id)
  const blocked = isBlocked(item, mscope.items, mscope.item_deps)
  const assetLabel = (id: string) => facts?.assets.find((a) => a.id === id)?.code ?? id
  const q = `?index=${indexId}`
  const q2 = (patch: Parameters<typeof updateItem>[3]) => updateItem(orgId, indexId, item.id, patch, actor)

  const tryClose = () => {
    try {
      closeItem(orgId, indexId, item.id, actor)
      setGateMsg(null)
    } catch (e) {
      setGateMsg(e instanceof CloseGateError ? `Refused · still vulnerable: ${e.failing_asset_ids.map(assetLabel).join(', ') || e.reason}` : String(e))
    }
  }

  return createPortal(
    <div className="rd fixed inset-0 z-[55]" data-testid="item-drawer">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="false" aria-label={`Migration item · ${item.code}`} tabIndex={-1} onKeyDown={onKey} className="absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col overflow-hidden outline-none" style={{ background: 'var(--rd-bg-header)', borderLeft: '1px solid var(--rd-border-control)', boxShadow: 'var(--rd-shadow-float)' }}>
        <div className="px-5 pb-4 pt-4" style={{ background: `${STATUS_TOKEN[item.status]}14`, borderBottom: '1px solid var(--rd-border-subtle)' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="rd-mono flex flex-wrap items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wider" style={{ color: 'var(--rd-text-muted)' }}>
                {item.code} · {system?.name ?? 'No system'}{wave ? ` · ${wave.label.toUpperCase()}` : ''}
                {item.created_from_group && <Chip tone="chip">GROUP</Chip>}
              </p>
              <h2 className="mt-1 text-[18px] font-bold leading-snug">{item.title}</h2>
              <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                {item.created_from_finding_id ? `Created from Discovery finding ${item.created_from_finding_id}` : item.created_from_group ? 'Created from a CBOM asset group' : 'Created manually'} · {item.owner_user_id ?? 'no owner'} · target {formatDate(item.target_date)}
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1" style={{ color: 'var(--rd-text-muted)' }} aria-label="Close drawer"><Icon name="x" size={18} /></button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* Status stepper */}
          <section>
            <div className="flex items-center justify-between"><p className="rd-label">Status</p><StatusChip status={item.status} /></div>
            <div className="mt-2 flex" style={{ gap: 4 }} role="group" aria-label="Status stepper">
              {ITEM_STATUSES.map((st, i) => {
                const done = i < stepIndex
                const cur = i === stepIndex
                const color = done ? 'var(--rd-status-ok)' : cur ? STATUS_TOKEN[st] : 'var(--rd-border-control)'
                const canMove = !closed && st !== item.status && (st !== 'closed' || gate.ok)
                return (
                  <button key={st} type="button" disabled={!canMove} onClick={() => (st === 'closed' ? tryClose() : setStatus(orgId, indexId, item.id, st, actor))} className="flex-1 text-left disabled:cursor-default" title={st === 'closed' && !gate.ok ? 'Closing runs the re-scan gate' : `Move to ${ITEM_STATUS_LABEL[st]}`} data-testid={`step-${st}`}>
                    <span className="block" style={{ height: 8, borderRadius: 4, background: color }} />
                    <span className="mt-1 block text-[11px]" style={{ color: cur ? 'var(--rd-text-primary)' : 'var(--rd-text-faint)', fontWeight: cur ? 600 : 400 }}>{ITEM_STATUS_LABEL[st]}</span>
                  </button>
                )
              })}
            </div>
            {blocked && <p className="mt-2 text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }}>Blocked on the roadmap: depends on {mscope.items.find((i) => i.id === dep?.depends_on_item_id)?.code}, which is not closed. Status is unchanged.</p>}
          </section>

          {/* Close-out gate (§3) */}
          {!closed && (
            <section className="rounded-[10px] p-3" style={{ background: 'var(--rd-tint-l1)', border: '1px solid var(--rd-tint-l1-edge)' }} data-testid="gate-panel">
              <div className="flex items-center justify-between gap-2">
                <p className="rd-label" style={{ color: 'var(--rd-status-warn)' }}>Close-out gate · re-scan</p>
                <span className="text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>{gate.scan_ref ? `Last scan ${gate.scan_ref.scan_id} · ${formatDate(gate.scan_ref.completed_at)}` : 'No re-scan yet'}</span>
              </div>
              <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>
                {gate.reason === 'no_assets' ? 'Add assets to the scope before closing.' : gate.reason === 'no_scan' ? `Closing requires a re-scan showing ${item.current_algorithm || 'the current algorithm'} absent on every scoped asset.` : gate.reason === 'stale_scan' ? 'The scope or target changed after the last scan. Request a new re-scan.' : gate.ok ? 'Every scoped asset is clear. The item can be closed.' : `${gate.failing_asset_ids.length} of ${scoped.length} assets still report ${item.current_algorithm || 'the vulnerable algorithm'}.`}
              </p>
              {gate.scan_ref && (
                <ul className="mt-2 space-y-1 text-[12.5px]">
                  {scoped.map((id) => {
                    const o = gate.scan_ref!.outcomes.find((x) => x.asset_id === id)
                    const bad = !o || o.vulnerable_present
                    return (
                      <li key={id} className="flex items-center gap-2">
                        <Icon name={bad ? 'warn' : 'check'} size={13} style={{ color: bad ? 'var(--rd-status-warn)' : 'var(--rd-status-ok-text)' }} />
                        <span className="rd-mono">{assetLabel(id)}</span>
                        <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--rd-text-muted)' }}>{facts?.assets.find((a) => a.id === id)?.model_version ?? 'manual row'}</span>
                        <span style={{ color: bad ? 'var(--rd-status-warn)' : 'var(--rd-status-ok-text)' }}>{bad ? 'still vulnerable' : 'clear'}</span>
                      </li>
                    )
                  })}
                </ul>
              )}
              {gateMsg && <p className="mt-2 text-[12.5px]" role="alert" style={{ color: 'var(--rd-status-warn)' }} data-testid="gate-error">{gateMsg}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="rd-btn" onClick={() => requestRescan(orgId, indexId, item.id, actor)} disabled={!scoped.length} data-testid="request-rescan"><Icon name="sync" size={14} /> Request re-scan</button>
                <button type="button" className="rd-btn rd-btn-primary" disabled={!gate.ok} onClick={tryClose} title={gate.ok ? 'Close this item' : 'Disabled while the gate fails'} data-testid="close-item"><Icon name="check" size={14} /> Close item</button>
              </div>
              <p className="mt-2 text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>Scanner: {facts?.scanner ?? 'Discovery (M1 F12)'} · a closed item cannot be reopened; a regression raises a new finding.</p>
            </section>
          )}

          {/* Details */}
          <section>
            <p className="rd-label">Details</p>
            <div className="mt-2 grid grid-cols-2 gap-3 text-[12.5px]">
              <div>
                <p className="flex items-center justify-between" style={{ color: 'var(--rd-text-muted)' }}>Current algorithm <Chip tone="ok">CBOM</Chip></p>
                <p className="rd-mono mt-1 text-[14px]">{item.current_algorithm || '—'}</p>
              </div>
              <div>
                <p className="flex items-center justify-between" style={{ color: 'var(--rd-text-muted)' }}>Target algorithm <Chip tone="dashed" title="Evaluation Template (FR-2.2) hook · pending its draft">TE Obj 1 eval</Chip></p>
                <input aria-label="Target algorithm" className="rd-input mt-1 rd-mono !text-[12.5px]" value={item.target_algorithm} disabled={closed} onChange={(e) => q2({ target_algorithm: e.target.value })} />
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>Owner</p>
                <input aria-label="Owner" className="rd-input mt-1 !text-[12.5px]" value={item.owner_user_id ?? ''} disabled={closed} onChange={(e) => q2({ owner_user_id: e.target.value || null })} />
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>Target date</p>
                <input aria-label="Target date" type="date" className="rd-input mt-1 rd-mono !text-[12.5px]" value={item.target_date ?? ''} disabled={closed || item.target_source === 'catalogue'} onChange={(e) => q2({ target_date: e.target.value || null })} />
                {/* I3 §8.2: the target follows the catalogue roadmap date unless the owner pins it. */}
                <label className="mt-1 flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                  <input type="checkbox" checked={item.target_source === 'catalogue'} disabled={closed} onChange={(e) => q2({ target_source: e.target.checked ? 'catalogue' : 'owner' })} data-testid="target-follows-catalogue" /> Follows the catalogue vendor date
                </label>
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>System › component</p>
                <p className="mt-1 text-[12.5px]">{facts?.systems.find((x) => x.id === item.system_id)?.name ?? '—'} › {facts?.components.find((c) => c.id === item.component_id)?.name ?? <span style={{ color: 'var(--rd-status-warn)' }}>no component</span>}</p>
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>Capability</p>
                <p className="mt-1 text-[12.5px]">{item.capability ? item.capability.replace(/_/g, ' ') : '—'}</p>
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>Wave</p>
                <select aria-label="Wave" className="rd-input mt-1 !text-[12.5px]" value={item.wave_id ?? ''} disabled={closed} onChange={(e) => q2({ wave_id: e.target.value || null })}>
                  <option value="">Unscheduled</option>
                  {(facts?.waves ?? []).map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
                </select>
              </div>
              <div>
                <p style={{ color: 'var(--rd-text-muted)' }}>Depends on</p>
                <select aria-label="Depends on" className="rd-input mt-1 !text-[12.5px]" value={dep?.depends_on_item_id ?? ''} disabled={closed} onChange={(e) => setDependency(orgId, indexId, item.id, e.target.value || null, actor)}>
                  <option value="">—</option>
                  {mscope.items.filter((i) => i.id !== item.id).map((i) => <option key={i.id} value={i.id}>{i.code} · {i.title}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <p style={{ color: 'var(--rd-text-muted)' }}>Vendor-gated release year <span style={{ color: 'var(--rd-text-faint)' }}>· when the target is a vendor release</span></p>
                <input aria-label="Vendor-gated year" type="number" className="rd-input mt-1 rd-mono !w-28 !text-[12.5px]" value={item.vendor_gated_year ?? ''} disabled={closed} onChange={(e) => q2({ vendor_gated_year: e.target.value ? Number(e.target.value) : null })} />
              </div>
            </div>
          </section>

          {/* Scope */}
          <section>
            <div className="flex items-center justify-between">
              <p className="rd-label">Scope · {scoped.length} {scoped.length === 1 ? 'asset' : 'assets'}{item.created_from_group ? ' · group' : ''}</p>
              {!closed && systemAssets.length > 0 && <button type="button" className="rd-link text-[12.5px]" onClick={() => setEditScope((v) => !v)} data-testid="edit-scope">{editScope ? 'Done' : 'Edit scope'}</button>}
            </div>
            {editScope ? (
              <ul className="mt-2 space-y-1 text-[12.5px]">
                {systemAssets.map((a) => (
                  <li key={a.id}>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={scoped.includes(a.id)} onChange={(e) => setScope(orgId, indexId, item.id, e.target.checked ? [...scoped, a.id] : scoped.filter((x) => x !== a.id), actor)} />
                      <span className="rd-mono">{a.code}</span> <span className="min-w-0 flex-1 truncate">{a.model_version}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="mt-2 space-y-1 text-[12.5px]" data-testid="scope-list">
                {scoped.length === 0 && <li style={{ color: 'var(--rd-text-muted)' }}>No assets in scope.</li>}
                {scoped.map((id) => {
                  const a = facts?.assets.find((x) => x.id === id)
                  return (
                    <li key={id} className="flex items-center gap-2 rounded-[8px] px-2.5 py-1.5" style={{ background: 'var(--rd-bg-card)', border: '1px solid var(--rd-border-subtle)' }}>
                      <span className="rd-mono">{a?.code ?? id}</span>
                      <span className="min-w-0 flex-1 truncate">{a?.model_version ?? 'manual row'}</span>
                      <span className="rd-mono" style={{ color: 'var(--rd-text-faint)' }}>{a?.current_algorithm}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {/* Agility (§4) */}
          {item.system_id && (
            <section>
              <p className="rd-label">Cryptographic agility · {system?.name}</p>
              <div className="mt-2 space-y-1.5 text-[12.5px]">
                {([['algorithm_configurable', 'Algorithms configurable without code change'], ['key_rotation_automated', 'Key rotation automated'], ['library_abstracted', 'Cryptographic library abstracted']] as const).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2">
                    <input type="checkbox" checked={!!agility?.[k]} onChange={(e) => setAgility(orgId, indexId, item.system_id!, { [k]: e.target.checked }, actor)} data-testid={`agility-${k}`} />
                    {label}
                  </label>
                ))}
                <p className="text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{agility?.set_at ? `Set by ${agility.set_by} · ${formatDate(agility.set_at)}` : 'Not yet assessed'} · feeds Part A 3. Cryptography Management and the TE objective signal.</p>
              </div>
            </section>
          )}

          {/* The submission plan */}
          <section className="rounded-[10px] p-3" style={{ background: 'var(--rd-bg-tile)' }} data-testid="feeds-plan">
            <p className="rd-label">Submission plan</p>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{system ? `${system.name}'s annex answers “Will this asset be migrated?” itself; this item does not fill it in.` : 'No system linked.'} {system?.cii && <Link to={`/readiness/submission/plan${q}`} className="rd-link">Open the plan →</Link>}</p>
          </section>

          <section>
            <p className="rd-label">History</p>
            <ul className="mt-1.5 space-y-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>
              {[...mscope.item_events.filter((e) => e.item_id === item.id)].reverse().slice(0, 6).map((e) => (
                <li key={e.id}>{formatDate(e.occurred_at)} · {e.event_type.replace(/_/g, ' ')}{e.from || e.to ? ` · ${e.from ?? ''}${e.from && e.to ? ' → ' : ''}${e.to ?? ''}` : ''}{e.note ? ` · ${e.note}` : ''} · {e.actor}</li>
              ))}
            </ul>
          </section>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 text-[12.5px]" style={{ borderTop: '1px solid var(--rd-border-subtle)', color: 'var(--rd-text-muted)' }}>
          <span className="min-w-0 truncate">Updated {fmtDay(item.updated_at)}</span>
          <span className="flex shrink-0 items-center gap-1">
            <button type="button" className="rd-btn rd-btn-sm" disabled={!prev} onClick={() => prev && onNavigate(prev)} aria-label={prev ? `Previous item` : 'No previous item'} data-testid="item-prev"><Icon name="chevron-left" size={13} /> Prev</button>
            <button type="button" className="rd-btn rd-btn-sm" disabled={!next} onClick={() => next && onNavigate(next)} aria-label={next ? `Next item` : 'No next item'} data-testid="item-next">Next <Icon name="chevron-right" size={13} /></button>
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}

export type { MigrationItemStatus }
